
/*
 * PERFORMANCE REEL MOBILE NAVIGATION
 * Two-level animated navigation for Wokiee.
 * Includes Google Store Quality badge handling.
 */

(function () {
  'use strict';

  function initializePRMenu() {
    const menu = document.getElementById('pr-mobile-menu');

    if (!menu || menu.dataset.initialized === 'true') return;

    const mainPanel = menu.querySelector('.pr-menu-main');
    const subPanels = Array.from(
      menu.querySelectorAll('.pr-menu-submenu')
    );
    const closeButton = menu.querySelector('.pr-menu-close');

    if (!mainPanel || !closeButton) return;

    menu.dataset.initialized = 'true';

    let activePanel = mainPanel;
    let previousFocus = null;
    let previousOverflow = '';
    let transitionTimer = null;
    let isTransitioning = false;

    let badgeObserver = null;
    const hiddenBadgeButtons = new Map();

    const allPanels = [mainPanel].concat(subPanels);

    /*
     * Hide only the floating Google Store Quality badge.
     * Does not affect the Google badge in the site header.
     */
    function updateGoogleBadgeVisibility(hide) {
      if (!hide) {
        if (badgeObserver) {
          badgeObserver.disconnect();
          badgeObserver = null;
        }

        hiddenBadgeButtons.forEach(function (previous, button) {
          if (previous.value) {
            button.style.setProperty(
              'display',
              previous.value,
              previous.priority
            );
          } else {
            button.style.removeProperty('display');
          }
        });

        hiddenBadgeButtons.clear();
        return;
      }

      function hideBadge() {
        document.querySelectorAll(
          'img[alt="Top quality store popup button"]'
        ).forEach(function (image) {
          const button = image.closest('button');

          if (!button) return;

          if (!hiddenBadgeButtons.has(button)) {
            hiddenBadgeButtons.set(button, {
              value: button.style.getPropertyValue('display'),
              priority: button.style.getPropertyPriority('display')
            });
          }

          button.style.setProperty(
            'display',
            'none',
            'important'
          );
        });
      }

      hideBadge();

      if (!badgeObserver) {
        badgeObserver = new MutationObserver(function () {
          hideBadge();
        });

        badgeObserver.observe(document.body, {
          childList: true,
          subtree: true
        });
      }
    }

    function getAnimationDuration() {
      if (window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches) {
        return 0;
      }

      const value = getComputedStyle(menu)
        .getPropertyValue('--pr-animation-speed')
        .trim();

      const duration = parseInt(value, 10);

      return Number.isFinite(duration)
        ? Math.max(0, Math.min(duration, 1200))
        : 450;
    }

    function resetPanels() {
      clearTimeout(transitionTimer);

      isTransitioning = false;

      allPanels.forEach(function (panel) {
        panel.style.transition = 'none';
        panel.style.transform = '';
        panel.style.visibility = '';
        panel.style.pointerEvents = '';
        panel.hidden = panel !== mainPanel;
        panel.classList.remove('pr-slide-out', 'pr-slide-in');
      });

      mainPanel.hidden = false;
      activePanel = mainPanel;
    }

    function openMenu() {
      if (window.innerWidth >= 1025 || !menu.hidden) return;

      previousFocus = document.activeElement;
      previousOverflow = document.body.style.overflow;

      resetPanels();

      menu.hidden = false;
      menu.setAttribute('aria-hidden', 'false');

      document.body.style.overflow = 'hidden';

      updateGoogleBadgeVisibility(true);

      closeButton.focus();
    }

    function closeMenu() {
      if (menu.hidden) return;

      clearTimeout(transitionTimer);

      menu.hidden = true;
      menu.setAttribute('aria-hidden', 'true');

      document.body.style.overflow = previousOverflow;

      updateGoogleBadgeVisibility(false);

      resetPanels();

      if (previousFocus && previousFocus.isConnected) {
        previousFocus.focus();
      }
    }

    function slideToPanel(nextPanel, direction) {
      if (isTransitioning || nextPanel === activePanel) return;

      const currentPanel = activePanel;
      const duration = getAnimationDuration();

      isTransitioning = true;

      nextPanel.hidden = false;
      nextPanel.scrollTop = 0;

      allPanels.forEach(function (panel) {
        if (panel !== currentPanel && panel !== nextPanel) {
          panel.hidden = true;
        }
      });

      const incomingOffset = direction === 'forward'
        ? '100%'
        : '-100%';

      const outgoingOffset = direction === 'forward'
        ? '-100%'
        : '100%';

      [currentPanel, nextPanel].forEach(function (panel) {
        panel.style.transition = 'none';
        panel.style.visibility = 'visible';
        panel.style.pointerEvents = 'none';
      });

      currentPanel.style.transform = 'translateX(0)';
      nextPanel.style.transform =
        'translateX(' + incomingOffset + ')';

      // Force layout before triggering the animation.
      void nextPanel.offsetWidth;

      const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
      const transition =
        'transform ' + duration + 'ms ' + easing;

      currentPanel.style.transition = transition;
      nextPanel.style.transition = transition;

      currentPanel.style.transform =
        'translateX(' + outgoingOffset + ')';

      nextPanel.style.transform = 'translateX(0)';

      function finishTransition() {
        currentPanel.hidden = true;

        currentPanel.style.transition = 'none';
        currentPanel.style.transform = '';
        currentPanel.style.visibility = '';
        currentPanel.style.pointerEvents = '';

        nextPanel.style.transition = 'none';
        nextPanel.style.transform = '';
        nextPanel.style.visibility = '';
        nextPanel.style.pointerEvents = '';

        activePanel = nextPanel;
        isTransitioning = false;
        transitionTimer = null;

        const focusTarget =
          nextPanel === mainPanel
            ? nextPanel.querySelector('.pr-menu-category')
            : nextPanel.querySelector('.pr-menu-back');

        if (focusTarget) focusTarget.focus();
      }

      if (duration === 0) {
        finishTransition();
      } else {
        transitionTimer = setTimeout(
          finishTransition,
          duration + 30
        );
      }
    }

    function openSubmenu(id) {
      const panel = subPanels.find(function (item) {
        return item.dataset.prPanel === id;
      });

      if (!panel) return;

      slideToPanel(panel, 'forward');
    }

    function returnToMain() {
      slideToPanel(mainPanel, 'backward');
    }

    // Intercept Wokiee's existing mobile hamburger.
    document.addEventListener('click', function (event) {
      const trigger = event.target.closest('.mainmenumob-js');

      if (trigger && window.innerWidth < 1025) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        openMenu();
      }
    }, true);

    closeButton.addEventListener('click', closeMenu);

    menu.querySelectorAll('[data-pr-open]').forEach(function (button) {
      button.addEventListener('click', function () {
        openSubmenu(button.dataset.prOpen);
      });
    });

    menu.querySelectorAll('.pr-menu-back').forEach(function (button) {
      button.addEventListener('click', returnToMain);
    });

    document.addEventListener('keydown', function (event) {
      if (menu.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        menu.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled])'
        )
      ).filter(function (item) {
        return item.getClientRects().length > 0 &&
          getComputedStyle(item).visibility !== 'hidden';
      });

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    });

    window.addEventListener('resize', function () {
      if (!menu.hidden && window.innerWidth >= 1025) {
        closeMenu();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializePRMenu
    );
  } else {
    initializePRMenu();
  }
})();
